import { request } from './client.js';
import type {
  Alert,
  AlertDetail,
  AlertEvent,
  AlertListParams,
  AlertMetrics,
  AlertPage,
  AlertStatus,
  AlertTitleResponse,
  CrossClientAlertPage,
} from './types.js';

/**
 * Ask for the advisory title of a multi-member alert (task 77b). 202
 * `pending` when the API claimed it and invoked the Lambda; 200 with the
 * current state when someone else did, it is already ready, or the alert
 * has one member (`reason: single_member`). Poll the detail.
 */
export function requestAlertTitle(
  clientTenantId: string,
  alertRef: string,
  idToken: string,
): Promise<AlertTitleResponse> {
  return request<AlertTitleResponse>(
    'POST',
    `${base(clientTenantId)}/${encodeURIComponent(alertRef)}/title`,
    idToken,
  );
}

function base(clientTenantId: string): string {
  return `/tenants/${encodeURIComponent(clientTenantId)}/alerts`;
}

/**
 * Every visible Client's queue, merged by score (task 66). Same filters as
 * `listAlerts`; the cursor is its own kind and only round-trips here.
 */
export function listAllAlerts(params: AlertListParams, idToken: string): Promise<CrossClientAlertPage> {
  return request<CrossClientAlertPage>('GET', '/alerts', idToken, { query: { ...params } });
}

export function listAlerts(
  clientTenantId: string,
  params: AlertListParams,
  idToken: string,
): Promise<AlertPage> {
  return request<AlertPage>('GET', base(clientTenantId), idToken, { query: { ...params } });
}

/** Means over the same filter set the list accepts (Story 17). */
export function getAlertMetrics(
  clientTenantId: string,
  params: Omit<AlertListParams, 'cursor' | 'limit'>,
  idToken: string,
): Promise<AlertMetrics> {
  return request<AlertMetrics>('GET', `${base(clientTenantId)}/metrics`, idToken, {
    query: { ...params },
  });
}

/** `alertRef` is the uuid or the `BSM-<n>` short id; both address the same alert. */
export function getAlert(
  clientTenantId: string,
  alertRef: string,
  idToken: string,
): Promise<AlertDetail> {
  return request<AlertDetail>(
    'GET',
    `${base(clientTenantId)}/${encodeURIComponent(alertRef)}`,
    idToken,
  );
}

/**
 * One move through Story 8's lifecycle. The API validates the transition
 * table; a 409 `illegal_transition` means the alert moved under you.
 */
export function transitionAlert(
  clientTenantId: string,
  alertRef: string,
  body: { to: AlertStatus; note?: string },
  idToken: string,
): Promise<Alert> {
  return request<Alert>(
    'POST',
    `${base(clientTenantId)}/${encodeURIComponent(alertRef)}/transition`,
    idToken,
    { body },
  );
}

/**
 * A note on the timeline that does not move the alert (Story 8). Legal in
 * every status, closed included; the API returns the recorded event.
 */
export function addAlertComment(
  clientTenantId: string,
  alertRef: string,
  note: string,
  idToken: string,
): Promise<AlertEvent> {
  return request<AlertEvent>(
    'POST',
    `${base(clientTenantId)}/${encodeURIComponent(alertRef)}/comments`,
    idToken,
    { body: { note } },
  );
}
